import { UserAction } from '#types/types';
import { v4 as uuidv4 } from 'uuid';

// components/useActionHistory.ts
type ActionHistoryReducer = (currentHistory: UserAction[], newAction: UserAction) => UserAction[];

const actionReducers: { [key: string]: ActionHistoryReducer } = {
  'Inpaint Tool Selected': (currentHistory, newAction) => {
    const filteredHistory = currentHistory.filter(action => action.name !== 'Inpaint Tool Selected');
    filteredHistory.push(newAction);
    return filteredHistory;
  },
  'Setting Changed': (currentHistory, newAction) => {
    const lastAction = currentHistory[currentHistory.length - 1];
    if (
      lastAction && lastAction.name === 'Setting Changed' &&
      lastAction.payload.modelId === newAction.payload.modelId &&
      lastAction.payload.parameterName === newAction.payload.parameterName
    ) {
      return [...currentHistory.slice(0, -1), newAction];
    }
    return [...currentHistory, newAction];
  },
  'default': (currentHistory, newAction) => {
    return [...currentHistory, newAction];
  },
  'Layer Visibility Toggled': (currentHistory, newAction) => {
    const lastAction = currentHistory[currentHistory.length - 1];
    const timeWindow = 5000;
    if (lastAction && lastAction.payload.layer_id === newAction.payload.layer_id) {
      const timeDiff = new Date(newAction.timestamp).getTime() - new Date(lastAction.timestamp).getTime();
      if (timeDiff < timeWindow) {
        if (lastAction.name === 'Layer Visibility Toggled') {
          const previousCount =
            typeof lastAction.payload.count === 'number' && Number.isFinite(lastAction.payload.count)
              ? Math.max(1, Math.floor(lastAction.payload.count))
              : 1;
          const updatedAction = {
            ...lastAction,
            id: newAction.id,
            timestamp: newAction.timestamp,
            payload: {
              layer_id: newAction.payload.layer_id,
              layer_name: newAction.payload.layer_name,
              count: previousCount + 1,
              final_state: newAction.payload.new_state,
            },
          };
          return [...currentHistory.slice(0, -1), updatedAction];
        }
      }
    }
    return [...currentHistory, newAction];
  },
};

export function getNextActionHistory(currentHistory: UserAction[], actionName: string, payload?: Record<string, any>): UserAction[] {
  const newAction: UserAction = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    name: actionName,
    payload: payload || {},
  };

  const reducer = actionReducers[newAction.name] || actionReducers['default'];
  const newHistory = reducer && reducer(currentHistory, newAction) || currentHistory;

  if (newHistory.length > 10000) {
    return newHistory.slice(newHistory.length - 10000);
  }
  return newHistory;
}

export function processActionHistoryForLLM(actions: UserAction[] | undefined, now: Date): string {
    if (!actions || actions.length === 0) {
        return "No recent actions recorded.";
    }
    const historyForProcessing = actions.slice(0, -1);
    if (historyForProcessing.length === 0) {
        return "No actions recorded before this turn.";
    }
    const recentActions = historyForProcessing.slice(-100);
    const nowMs = now.getTime();
    const formattedLines = recentActions.map((action) => {
        const relativeSeconds = Math.round((nowMs - new Date(action.timestamp).getTime()) / 1000);
        const timePrefix = `-${relativeSeconds}s`;
        const { name, payload } = action;
        let details = '';
        switch (name) {
            case 'Workspace Tab Changed': details = `Switched to workspace: ${payload.new_workspace_id}`; break;
            case 'Inpaint Tool Selected': details = `Selected tool: ${payload.tool_name}`; break;
            case 'Task Result Selected': details = `Selected a past generation result (Task ID: ${payload.taskId})`; break;
            case 'Setting Changed': details = `Parameter '${payload.parameterName}' in model '${payload.modelId}' set to ${JSON.stringify(payload.value)}`; break;
            case 'Layer Visibility Toggled':
              if (typeof payload.count === 'number' && payload.count > 1) {
                details = `Layer '${payload.layer_name}' visibility toggled ${payload.count} times, ending in state: ${payload.final_state}`;
              } else {
                details = `Layer '${payload.layer_name}' visibility set to ${payload.new_state}`;
              }
              break;
            case 'Chat Message Queued': details = `User sent a message to the assistant`; break;
            case 'Generation Initiated': details = `Initiated image generation with model '${payload.model_id}'`; break;
            default: details = `Performed action: '${name}'`;
        }
        return `${timePrefix} ${details}`;
    });
    return formattedLines.join('\n');
}
